/**
 * PayerOverviewGrid Component
 *
 * Card-based overview of all payers. Shows key stats at a glance:
 * status, renewal date, filing window, auth burden, number of flags.
 * Click a card to drill into that payer.
 */

import React from 'react';
import { Box, Grid, Paper, Typography, useTheme } from '@mui/material';
import type { PayerContract } from '../types';
import {
  calculateNextExpirationDate,
  calculateAuthBurdenScore,
  calculateCashflowRiskScore,
} from '../hooks/useContractAnalytics';

interface PayerOverviewGridProps {
  payers: PayerContract[];
  onSelectPayer: (payerId: string) => void;
}

function formatRenewalDate(payer: PayerContract): string {
  const c = payer.contract;
  const exp = calculateNextExpirationDate(c.effective_date, c.initial_term_years, c.renewal_term_years, c.renewal_mechanism);
  if (!exp) return 'Unknown';
  return exp.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function getStatusLabel(payer: PayerContract): { label: string; color: string } {
  const c = payer.contract;
  const exp = calculateNextExpirationDate(c.effective_date, c.initial_term_years, c.renewal_term_years, c.renewal_mechanism);
  if (!exp) return { label: 'Unknown', color: '#9e9e9e' };
  const days = Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: 'Expired', color: '#f44336' };
  if (days <= 90) return { label: 'Expiring Soon', color: '#ff9800' };
  return { label: 'Active', color: '#4caf50' };
}

export const PayerOverviewGrid: React.FC<PayerOverviewGridProps> = ({
  payers,
  onSelectPayer,
}) => {
  const theme = useTheme();

  return (
    <Grid container spacing={2}>
      {payers.map((payer) => {
        const status = getStatusLabel(payer);
        const auth = calculateAuthBurdenScore(payer);
        const risk = calculateCashflowRiskScore(payer);
        const flagCount = (payer.audit_flags || []).filter(f => f.severity === 'critical' || f.severity === 'high').length;
        const rateCount = payer.reimbursement.rates?.length || 0;

        return (
          <Grid item xs={12} sm={6} md={4} key={payer.payer.name}>
            <Paper
              elevation={0}
              onClick={() => onSelectPayer(payer.payer.name)}
              sx={{
                p: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}20`,
                },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Header: name + status dot */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {payer.payer.name}
                </Typography>
              </Box>

              {/* Subtitle: type + state */}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                {payer.payer.payer_type.replace(/_/g, ' ')} &middot; {payer.payer.state}
              </Typography>

              {/* Stats grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1.5,
                  mt: 'auto',
                }}
              >
                <StatItem label="Renewal" value={formatRenewalDate(payer)} />
                <StatItem label="Filing" value={`${payer.claims_submission.timely_filing_days}d`} />
                <StatItem label="Auth" value={auth.level} />
                <StatItem label="Risk" value={risk.level} />
                <StatItem label="Rates" value={String(rateCount)} />
                <StatItem label="Flags" value={flagCount > 0 ? String(flagCount) : '\u2014'} />
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

const StatItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, lineHeight: 1 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, mt: 0.125, textTransform: 'capitalize' }}>
      {value}
    </Typography>
  </Box>
);
